import React, { useState } from 'react';
import { Send, Mail, Facebook, CheckCircle } from 'lucide-react';
import { mockData } from '../mock';
import { toast } from '../hooks/use-toast';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. We'll get back to you soon.",
      });
      setFormData({ name: '', email: '', company: '', message: '' });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <section className="bg-gradient-to-b from-black via-gray-950 to-black py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column - CTA */}
          <div className="space-y-8">
            <div>
              <h2 className="text-5xl md:text-6xl font-bold mb-4">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-400 to-amber-600">
                  Let's Create
                </span>
              </h2>
              <div className="h-1 w-24 bg-gradient-to-r from-amber-400 to-transparent mb-8"></div>
            </div>

            <p className="text-xl text-gray-300 leading-relaxed">
              Ready to bring your vision to life with cinematic storytelling? Let's collaborate on your next project.
            </p>

            <div className="space-y-6 pt-8">
              <a
                href={`mailto:${mockData.director.email}`}
                className="group flex items-center space-x-4 text-gray-300 hover:text-amber-400 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-amber-400/10 flex items-center justify-center group-hover:bg-amber-400/20 transition-all group-hover:scale-110">
                  <Mail className="text-amber-400" size={20} />
                </div>
                <div>
                  <div className="text-sm text-gray-500 uppercase tracking-wider">Email</div>
                  <div className="font-medium">{mockData.director.email}</div>
                </div>
              </a>

              <a
                href={mockData.director.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center space-x-4 text-gray-300 hover:text-amber-400 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-amber-400/10 flex items-center justify-center group-hover:bg-amber-400/20 transition-all group-hover:scale-110">
                  <Facebook className="text-amber-400" size={20} />
                </div>
                <div>
                  <div className="text-sm text-gray-500 uppercase tracking-wider">Social</div>
                  <div className="font-medium">Facebook</div>
                </div>
              </a>
            </div>

            {/* Testimonial */}
            <div className="mt-12 p-6 border-l-2 border-amber-400 bg-gradient-to-br from-gray-900/50 to-transparent rounded-r-lg">
              <p className="text-gray-300 italic mb-4">
                "{mockData.testimonials[0].text}"
              </p>
              <p className="text-amber-400 text-sm font-semibold">
                — {mockData.testimonials[0].author}
              </p>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="relative">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-2 uppercase tracking-wider">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2 uppercase tracking-wider">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-400 mb-2 uppercase tracking-wider">
                  Company / Organization
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors"
                  placeholder="Your company name"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-400 mb-2 uppercase tracking-wider">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                  placeholder="Tell us about your project..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold py-4 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 uppercase tracking-wider"
              >
                {isSubmitting ? (
                  <>
                    <CheckCircle className="animate-spin" size={20} />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <span>Start a Project</span>
                    <Send size={20} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;